// FNV-1a 32-bit hash
function fnv32(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h.toString(16).toUpperCase().padStart(8, '0');
}

const SALT = 'MW@f1n4nc4s#2025';

const PLAN_CODE = { B: 'basico', P: 'pro', F: 'familia', E: 'essencial', C: 'completo' };
const CODE_PLAN = { basico: 'B', pro: 'P', familia: 'F', essencial: 'E', completo: 'C' };

// ── Estrutura de planos v2 (07/2026) ─────────────────────────────────────────
// Essencial: vida financeira completa. Completo: + IA, Família, relatórios avançados.
// Planos vitalícios legados (basico/pro/familia) são honrados PARA SEMPRE:
//   basico → recursos do Essencial | pro e familia → recursos do Completo
const ESSENCIAL_FEATURES = ['dashboard', 'profile', 'accounts', 'transactions', 'categories', 'calendar', 'reports', 'month-report', 'investments', 'goals', 'budgets', 'salary-division', 'forecasts', 'installments', 'subscriptions', 'debts', 'vehicles', 'help', 'getting-started', 'plans'];
const COMPLETO_FEATURES  = [...ESSENCIAL_FEATURES, 'card-report', 'tax-report', 'ai-consultor', 'family', 'whatsapp'];

export const PLAN_FEATURES = {
  essencial: ESSENCIAL_FEATURES,
  completo:  COMPLETO_FEATURES,
  // Legados (vitalícios) — mapeados para os equivalentes novos
  basico:  ESSENCIAL_FEATURES,
  pro:     COMPLETO_FEATURES,
  familia: COMPLETO_FEATURES,
};

export const PLAN_LABEL = {
  essencial: 'Essencial',
  completo:  'Completo',
  basico:  'Básico (vitalício)',
  pro:     'Pro (vitalício)',
  familia: 'Família (vitalício)',
};

export const PLAN_PRICING = {
  essencial: { monthly: 14.90, yearly: 147 },
  completo:  { monthly: 29.90, yearly: 297 },
};

// Licença expirada? (licenças vitalícias não têm expiresAt — nunca expiram)
export function isLicenseExpired(license) {
  if (!license?.expiresAt) return false;
  return new Date(license.expiresAt).getTime() < Date.now();
}

// Validates key and returns plan string, or null if invalid
export function validateKey(raw) {
  const key = raw.trim().toUpperCase();
  // Expected: MAW-B-XXXXXX-YYYYYYYY
  const parts = key.split('-');
  if (parts.length !== 4) return null;
  const [prefix, planCode, random, checksum] = parts;
  if (prefix !== 'MAW') return null;
  if (!PLAN_CODE[planCode]) return null;
  if (random.length !== 6) return null;
  if (checksum.length !== 8) return null;
  const expected = fnv32(planCode + random + SALT);
  if (checksum !== expected) return null;
  return PLAN_CODE[planCode];
}

export const TRIAL_DAYS = 7;

export function getTrialStatus(user, license) {
  // Licença expirada é tratada como ausente (volta para regras de trial/bloqueio)
  if (license && !isLicenseExpired(license)) return { active: false, expired: false, daysLeft: 0, hasLicense: true };
  if (!user?.createdAt) return { active: true, expired: false, daysLeft: TRIAL_DAYS };
  const diffDays = (Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24);
  const daysLeft = Math.max(0, Math.ceil(TRIAL_DAYS - diffDays));
  return { active: daysLeft > 0, expired: daysLeft === 0, daysLeft, hasLicense: false };
}

export function hasFeature(plan, feature, isTrial = false) {
  if (feature === 'plans') return true; // tela de planos é sempre acessível
  if (isTrial) return true;
  if (!plan) return false;
  return (PLAN_FEATURES[plan] || []).includes(feature);
}

export function loadLicense() {
  // Helper: rebuild and re-save from a raw key
  const rebuild = (k) => {
    const plan = validateKey(k);
    if (!plan) return null;
    const data = { key: k, plan, activatedAt: new Date().toISOString() };
    try { localStorage.setItem('maw_license', JSON.stringify(data)); } catch {}
    return data;
  };

  // 1. Primary storage
  try {
    const raw = localStorage.getItem('maw_license');
    if (raw) {
      const data = JSON.parse(raw);
      // Assinatura (Kiwify): sem chave offline — validada pela nuvem a cada login
      if (data.source === 'subscription' && data.plan && !isLicenseExpired(data)) return data;
      if (data.key && data.plan && validateKey(data.key) === data.plan && !isLicenseExpired(data)) return data;
    }
  } catch {}

  // 2. Credentials backup (written at login/register time)
  try {
    const creds = JSON.parse(localStorage.getItem('maw_credentials') || 'null');
    if (creds?.licenseKey) {
      const r = rebuild(creds.licenseKey);
      if (r) return r;
    }
  } catch {}

  // 3. Standalone key backup (written at activation time, independent of credentials)
  try {
    const k = localStorage.getItem('maw_k');
    if (k) {
      const r = rebuild(k);
      if (r) return r;
    }
  } catch {}

  return null;
}

export function saveLicense(key, plan) {
  const data = { key, plan, activatedAt: new Date().toISOString() };
  localStorage.setItem('maw_license', JSON.stringify(data));
  // Standalone backup — always written, independent of credentials
  localStorage.setItem('maw_k', key);
  // Also backup inside credentials if they already exist
  try {
    const creds = JSON.parse(localStorage.getItem('maw_credentials') || 'null');
    if (creds) {
      localStorage.setItem('maw_credentials', JSON.stringify({ ...creds, licenseKey: key }));
    }
  } catch {}
}

// ── Key generator (run in Node, not imported by app) ────────────────────────
// node -e "require('./src/utils/license.js').generateKeys('pro', 10)"
export function generateKeys(planName, count = 10) {
  const code = CODE_PLAN[planName];
  if (!code) throw new Error('Plano inválido: basico | pro | familia | essencial | completo');
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const keys = [];
  for (let i = 0; i < count; i++) {
    let random = '';
    for (let j = 0; j < 6; j++) random += chars[Math.floor(Math.random() * chars.length)];
    const checksum = fnv32(code + random + SALT);
    keys.push(`MAW-${code}-${random}-${checksum}`);
  }
  return keys;
}
